import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, setSelf, setToken } from '../services/client';
import type { EmployeeListItem } from '../services/types';
import { BilingualText } from '../components/BilingualText';
import { BilingualButton } from '../components/BilingualButton';
import { BrandHeader } from '../components/BrandHeader';
import { dict } from '../i18n/dictionary';

const PIN_LENGTH = 4;

export function PinEntry() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<EmployeeListItem | null>(null);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<{ ja: string; vi: string } | null>(null);

  useEffect(() => {
    api
      .listEmployees()
      .then((list) => {
        const found = list.find((e) => e.id === employeeId);
        if (!found) navigate('/');
        else setEmployee(found);
      })
      .catch((e: unknown) => {
        // 401 は services/client.ts で `/?expired=1` にリダイレクト済み
        if (e instanceof ApiError && e.status === 401) return;
        setErrorMsg({ ja: dict.networkError.ja, vi: dict.networkError.vi });
      });
  }, [employeeId, navigate]);

  const submit = async (fullPin: string): Promise<void> => {
    if (!employeeId) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const auth = await api.verifyPin(employeeId, fullPin);
      setToken(auth.token);
      setSelf(auth.employee);
      // 初期PIN (管理者発行のまま) なら強制的に変更画面へ
      if (auth.employee.pin_is_initial) {
        navigate('/change-pin?initial=1');
      } else {
        navigate('/punch');
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body?.error;
        if (body?.code === 'PIN_LOCKED') {
          setErrorMsg({ ja: dict.pinLocked.ja, vi: dict.pinLocked.vi });
        } else if (body?.code === 'INVALID_PIN') {
          const left = body.remaining_attempts;
          setErrorMsg({
            ja: `${dict.pinWrong.ja}${left !== undefined ? ` (${dict.remainingAttempts.ja}: ${left})` : ''}`,
            vi: `${dict.pinWrong.vi}${left !== undefined ? ` (${dict.remainingAttempts.vi}: ${left})` : ''}`,
          });
        } else {
          setErrorMsg({
            ja: body?.message_ja ?? dict.error.ja,
            vi: body?.message_vi ?? dict.error.vi,
          });
        }
      } else {
        setErrorMsg({ ja: dict.networkError.ja, vi: dict.networkError.vi });
      }
      setPin('');
    } finally {
      setSubmitting(false);
    }
  };

  const onDigit = (d: string): void => {
    if (submitting) return;
    const next = (pin + d).slice(0, PIN_LENGTH);
    setPin(next);
    if (next.length === PIN_LENGTH) {
      void submit(next);
    }
  };

  const onBackspace = (): void => {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
  };

  return (
    <main className="page page-pin">
      <BrandHeader />
      <header className="page-header">
        <button className="link-back" onClick={() => navigate('/')}>
          <BilingualText value={dict.back} separator="slash" />
        </button>
        {employee && (
          <div className="employee-display">
            <div className="name-ja">{employee.name_ja}</div>
            {employee.name_vi && <div className="name-vi">{employee.name_vi}</div>}
          </div>
        )}
        <h2>
          <BilingualText value={dict.enterPin} />
        </h2>
        <p className="hint">
          <BilingualText value={dict.pinHint} separator="slash" />
        </p>
      </header>

      <div className="pin-dots">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span
            key={i}
            className={`pin-dot ${i < pin.length ? 'filled' : ''}`}
            aria-hidden
          />
        ))}
      </div>

      {errorMsg && (
        <div className="error-banner" role="alert">
          <span className="bi-ja">{errorMsg.ja}</span>
          <span className="bi-vi">{errorMsg.vi}</span>
        </div>
      )}

      <div className="numpad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button
            key={n}
            className="numpad-key"
            onClick={() => onDigit(String(n))}
            disabled={submitting}
          >
            {n}
          </button>
        ))}
        <span className="numpad-blank" aria-hidden />
        <button
          className="numpad-key"
          onClick={() => onDigit('0')}
          disabled={submitting}
        >
          0
        </button>
        <button
          className="numpad-key numpad-back"
          onClick={onBackspace}
          disabled={submitting}
          aria-label="backspace"
        >
          ⌫
        </button>
      </div>
    </main>
  );
}
